import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Spin,
  Tooltip,
  Select,
} from "antd";
import ApiService from "../Components/apiService";
import { InfoCircleOutlined } from "@ant-design/icons";
import { BASE_URL } from "../Components/Constant";

const CreateNewButton = ({
  onCancel,
  isEdit,
  editingData,
  setIsCreatingOrEditing,
  isCreatingOrEditing,
  object,
  fetchCustomButtons,
  type, // 🔥 Add type prop ('component' or 'url')
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [classes, setClasses] = useState([]);
  const [methods, setMethods] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [methodArgs, setMethodArgs] = useState([]);
  const { Option } = Select;

  useEffect(() => {
    console.log("type in CreateNewButton:", type);
    
    if (editingData) {
      form.setFieldsValue({
        name: editingData.name,
        apiName: editingData.apiName,
        description: editingData.description || '',
        component: editingData.component || '',
        class: editingData?.action_info?.class_name || null,
        method: editingData?.action_info?.method_name || null,
      });
      
      setSelectedClass(editingData?.action_info?.class_name || null);
      setSelectedMethod(editingData?.action_info?.method_name || null);
      
      // If we have class and method, fetch methods and set arguments
      if (editingData?.action_info?.class_name && editingData?.action_info?.method_name) {
        const initializeEditData = async () => {
          try {
            const fetchedMethods = await fetchMethods(editingData.action_info.class_name);
            setMethods(fetchedMethods);
            
            // Find the selected method and set its arguments
            const selectedMethodData = fetchedMethods.find(m => m.name === editingData.action_info.method_name);
            if (selectedMethodData && selectedMethodData.args) {
              setMethodArgs(selectedMethodData.args);
              
              // Set argument values from editingData
              if (editingData.action_info.args && editingData.action_info.args.length > 0) {
                const argValues = {};
                editingData.action_info.args.forEach((argValue, index) => {
                  argValues[`arg_${index}`] = argValue;
                });
                form.setFieldsValue(argValues);
              }
            }
          } catch (error) {
            console.error('Error initializing edit data:', error);
          }
        };
        
        initializeEditData();
      }
    }
  }, [editingData, form]);

  useEffect(() => {
    console.log("editingData in CreateNewButton:", editingData);
    
    // Only fetch classes if we're not editing or if we don't have a selected class
    if (!editingData || !editingData?.action_info?.class_name) {
      fetchClasses().then((fetchedClasses) => {
        setClasses(fetchedClasses);
      });
    } else {
      // If editing, still fetch classes but don't override the selected values
      fetchClasses().then((fetchedClasses) => {
        setClasses(fetchedClasses);
      });
    }
  }, [editingData]);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const apiService = new ApiService(
        `${BASE_URL}/ruby_class`,
        { 'Content-Type': 'application/json' },
        'GET'
      );
      const response = await apiService.makeCall();
      return response?.classes?.map((item) => ({
        name: item.class_name
      }));
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMethods = async (className) => {
    setLoading(true);
    try {
      const apiService = new ApiService(
        `${BASE_URL}/api/classes/available_methods/${className}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );
      const response = await apiService.makeCall();
      return response?.methods || [];
    } catch (error) {
      console.error('Error fetching methods:', error);
      message.error('Failed to fetch methods. Please try again.');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = async (className) => {
    setSelectedClass(className);
    if (className) {
      const fetchedMethods = await fetchMethods(className);
      setMethods(fetchedMethods);
    } else {
      setMethods([]);
    }
    // Clear method and arguments when class changes
    form.setFieldsValue({ method: undefined });
    setSelectedMethod(null);
    setMethodArgs([]);
    // Clear all argument fields
    const currentValues = form.getFieldsValue();
    Object.keys(currentValues).forEach(key => {
      if (key.startsWith('arg_')) {
        form.setFieldsValue({ [key]: undefined });
      }
    });
  };

  const handleMethodChange = (methodName) => {
    setSelectedMethod(methodName);
    if (methodName) {
      const method = methods.find(m => m.name === methodName);
      if (method && method.args) {
        setMethodArgs(method.args);
      } else {
        setMethodArgs([]);
      }
    } else {
      setMethodArgs([]);
    }
    // Clear argument fields when method changes
    const currentValues = form.getFieldsValue();
    Object.keys(currentValues).forEach(key => {
      if (key.startsWith('arg_')) {
        form.setFieldsValue({ [key]: undefined });
      }
    });
  };

  const handleSave = async () => {
    setLoading(true);
    const values = await form.validateFields();
    
    // Collect method arguments as an array
    const methodArguments = [];
    methodArgs.forEach((arg, index) => {
      const argValue = values[`arg_${index}`];
      if (argValue) {
        methodArguments.push(argValue); // Push values in order
      }
    });

    try {
      const payload = {
        mt_object_id: object.object._id,
        action_button: {
          name: values.name,
          apiname: values.apiName,
          type: type,
          component_name: values.component || null,
          description: values.description || '',
          action_info: {
            class_name: selectedClass || null,
            method_name: selectedMethod || null,
            args: methodArguments.length > 0 ? methodArguments : null, // Pass as array
          },
        },
      };
      
      let apiService;
      if (editingData && editingData._id) {
        apiService = new ApiService(
          `${BASE_URL}/edit_button/${editingData._id}`,
          {},
          "PATCH",
          payload
        );
      } else {
        apiService = new ApiService(
          `${BASE_URL}/create_action_button`,
          {},
          "POST",
          payload
        );
      }

      await apiService.makeCall();
      message.success(isEdit ? "Button updated successfully!" : "Button created successfully!");
      fetchCustomButtons();
    } catch (error) {
      message.error(isEdit ? "Failed to update button." : "Failed to create button.");
    } finally {
      setLoading(false);
      setIsCreatingOrEditing(false);
    }
  };

  const handleNameBlur = () => {
    const nameValue = form.getFieldValue("name");
    if (nameValue) {
      const apiName = nameValue
        .replace(/[^a-zA-Z0-9]/g, "")
        .replace(/\s+/g, "_")
        .trim();
      form.setFieldsValue({ apiName });
    }
  };

  const handleCancel = () => {
    setIsCreatingOrEditing(false);
    onCancel();
  };

  return (
    <Modal
      title="Button Details"
      open={isCreatingOrEditing}
      onCancel={handleCancel}
      zIndex={1002}   // higher than sidebar
      footer={null}
      destroyOnClose
    >
      {loading ? (
        <Spin
          tip="Saving..."
          style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}
        />
      ) : (
        <Form form={form} onFinish={handleSave} layout="vertical">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please input the button name!" }]}
            required
          >
            <Input
              onBlur={handleNameBlur}
              placeholder="Enter button name"
            />
          </Form.Item>

          <Form.Item
            label="API Name"
            name="apiName"
            rules={[{ required: true, message: "Please enter an API name" }]}
            required
          >
            <Input placeholder="Enter API name" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <Input.TextArea
              placeholder="Enter description"
            />
          </Form.Item>

          {/* 🔥 Dynamically handle Component/URL */}
          <Form.Item
            label={
              <span>
                {type === "url" ? "Enter URL" : "Component Name"}&nbsp;
                <Tooltip
                  title={
                    type === "url"
                      ? (
                        <>
                          Format: <br />
                          <b>
                            ComponentName?primary_object=ObjName&template_id=templateId&download=true&attached=true
                          </b>
                        </>
                      )
                      : "Example: GenerateInvoice"
                  }
                  overlayInnerStyle={{
                    width: type === "url" ? "554px" : "auto",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            }
            name="component"
            rules={[
              {
                required: true,
                message:
                  type === "url"
                    ? "Please enter URL Format!"
                    : "Please enter Component Name!",
              },
            ]}
            required
          >
            <Input.TextArea
              placeholder={
                type === "url"
                  ? "e.g. ComponentName?primary_object=ObjName&template_id=templateId&download=true&attached=true"
                  : "e.g. InvoiceComponent"
              }
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </Form.Item>

          { type === "custom" && (
          <>
            <Form.Item
              label="Class"
              name="class"
              rules={[{ required: false, message: "Optional Ruby Class" }]}
            >
              <Select
                placeholder="Select Ruby Class"
                style={{ width: "100%" }}
                allowClear
                onChange={handleClassChange}
              >
                {classes.map((cls, index) => (
                  <Option key={index} value={cls.name}>
                    {cls.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Method"
              name="method"
              rules={[{ required: false, message: "Optional Method" }]}
            >
              <Select
                placeholder="Select Method"
                style={{ width: "100%" }}
                allowClear
                disabled={!selectedClass}
                onChange={handleMethodChange}
              >
                {methods.map((method, index) => (
                  <Option key={index} value={method.name}>
                    {method.name}
                    {method.args && method.args.length > 0 && (
                      <span style={{ color: '#666', fontSize: '12px' }}>
                        ({method.args.map(arg => arg.join(': ')).join(', ')})
                      </span>
                    )}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {methodArgs.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ marginBottom: '12px', color: '#333' }}>Method Arguments:</h4>
                {methodArgs.map((arg, index) => (
                  <Form.Item
                    key={index}
                    label={
                      <span>
                        {arg[1]} 
                        <span style={{ color: '#999', fontSize: '12px' }}>
                          ({arg[0]})
                        </span>
                      </span>
                    }
                    name={`arg_${index}`}
                    rules={[
                      {
                        required: arg[0] === 'req',
                        message: `Please enter value for ${arg[1]}!`
                      }
                    ]}
                    required={arg[0] === 'req'}
                  >
                    <Input
                      placeholder={`Enter ${arg[1]}`}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                ))}
              </div>
            )}
          </>
          )}

          <Form.Item>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </div>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default CreateNewButton;
